import React, { useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { Area, AreaChart, ResponsiveContainer } from 'recharts';
import { LockClosedIcon } from '@heroicons/react/24/outline';
import { slideUp } from '@/lib/motion';
import PageHeader from '@/components/common/PageHeader';
import { cn } from '@/lib/utils';
import { useCourses } from '../../services/courses';
import { usePrograms } from '../../services/programs';
import { useProgramAnalytics } from '../../services/analytics';
import ProgramAnalyticsPanel from './ProgramAnalyticsPanel';
import {
  FEATURE,
  normalizeTierId,
  planHasFeature,
  type TierId,
} from '@/components/settings/tiers/tierCatalog';
import { downloadProgramWorkbook } from './exportProgramWorkbook';
import { useAssignableLearners } from '../../hooks/useAssignableLearners';
import { useKpis, fetchCourseLearnerMetrics, KpiFilters } from '../../services/analytics';
import { useSubscription } from '../../services/billing';
import ResultsVsAverageChart from '../../components/analytics/ResultsVsAverageChart';
import DatePicker from '../../components/common/DatePicker';
import EntityFilterSelect from '../../components/common/EntityFilterSelect';
import { downloadWorkbook } from './exportWorkbook';
import {
  AhtValue,
  AnalyticsRange,
  ResultsVsAverage,
} from '../../types/analytics';

const ALL_COURSES = 'all';
const ALL_LEARNERS = 'all';

const EMPTY_RESULTS: ResultsVsAverage = {
  learner: { name: '', found: false },
  course: null,
  metrics: [],
};

// --- formatting helpers -----------------------------------------------------

const formatPct = (value: number | null | undefined): string =>
  value === null || value === undefined ? '—' : `${value}%`;

const formatFeedback = (value: number | null | undefined): string =>
  value === null || value === undefined ? '—' : `${(value * 2).toFixed(1)}/10`;

/** Exact duration — "47 s", "1 min 23 s", "2 h 5 min" — never rounded away. */
const formatDuration = (seconds: number): string => {
  const s = Math.max(0, Math.round(seconds));
  if (s < 60) return `${s} s`;
  const minutes = Math.floor(s / 60);
  const rest = s % 60;
  if (minutes < 60) return rest ? `${minutes} min ${rest} s` : `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const restMin = minutes % 60;
  return restMin ? `${hours} h ${restMin} min` : `${hours} h`;
};

const formatAht = (value: AhtValue | null | undefined): string => {
  if (!value || value.active_seconds === null) return '—';
  return formatDuration(value.active_seconds);
};

const formatTrend = (trend: number | null | undefined): string => {
  if (trend === null || trend === undefined) return 'No trend';
  const sign = trend > 0 ? '+' : '';
  return `${sign}${trend}%`;
};

// --- Sparkline overlay inside KPI cards ------------------------------------

type SparkPoint = { v: number };

const MiniSparkline: React.FC<{ points: SparkPoint[]; gradId: string }> = ({
  points,
  gradId,
}) => {
  if (points.length < 2) return null;
  const data = points.map((p, i) => ({ i, v: p.v }));
  return (
    <ResponsiveContainer width="100%" height="100%">
      <AreaChart data={data} margin={{ top: 0, right: 0, bottom: 0, left: 0 }}>
        <defs>
          <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="white" stopOpacity={0.28} />
            <stop offset="100%" stopColor="white" stopOpacity={0} />
          </linearGradient>
        </defs>
        <Area
          type="monotone"
          dataKey="v"
          stroke="rgba(255,255,255,0.55)"
          strokeWidth={1.5}
          fill={`url(#${gradId})`}
          dot={false}
          isAnimationActive={false}
        />
      </AreaChart>
    </ResponsiveContainer>
  );
};

// --- KPI card ---------------------------------------------------------------

interface KPICardProps {
  title: string;
  value: string;
  subtitle?: string | null;
  trend: string;
  color: 'orange' | 'purple' | 'gray' | 'green';
  loading?: boolean;
  sparkPoints?: SparkPoint[];
}

const KPICard: React.FC<KPICardProps> = ({
  title,
  value,
  subtitle,
  trend,
  color,
  loading,
  sparkPoints,
}) => {
  const colorClasses = {
    orange: 'bg-gradient-to-br from-warning-400 to-warning-600 dark:from-warning-500 dark:to-warning-700',
    purple: 'bg-gradient-to-br from-primary-500 to-primary-700 dark:from-primary-600 dark:to-primary-800',
    gray: 'bg-gradient-to-br from-grey-300 to-grey-500 dark:from-grey-600 dark:to-grey-800',
    green: 'bg-gradient-to-br from-success-400 to-success-600 dark:from-success-500 dark:to-success-700',
  };

  const hasSparkline = !loading && sparkPoints && sparkPoints.length >= 2;

  return (
    <div className={cn(
      colorClasses[color],
      'rounded-xl p-6 text-white relative overflow-hidden transition-shadow duration-200'
    )}>
      <div className="relative z-10">
        <div className="flex items-start justify-between mb-1">
          <div>
            <p className="text-white/80 text-sm mb-1">{title}</p>
            {loading ? (
              <div className="h-9 w-24 bg-white/20 rounded animate-pulse" />
            ) : (
              <>
                <h3 className="text-3xl font-bold">{value}</h3>
                {subtitle && <p className="text-white/70 text-xs mt-0.5">{subtitle}</p>}
              </>
            )}
          </div>
          {!loading && <span className="text-sm text-white/90">{trend}</span>}
        </div>
      </div>

      {hasSparkline && (
        <div className="absolute bottom-0 left-0 right-0 h-16 opacity-50 pointer-events-none">
          <MiniSparkline points={sparkPoints!} gradId={`spark-${color}`} />
        </div>
      )}
    </div>
  );
};

// --- Date presets -----------------------------------------------------------
//
// Tier policy mirrors the backend (`app/api/routes/analytics.py`) and the
// published offer (tierCatalog.ts):
//   free   — `weekly` only (7-day dashboard), no custom dates
//   growth — weekly + monthly (30-day dashboard), no custom dates
//   mid    (plan pro)        — all presets, no custom dates
//   high   (plan enterprise) — all presets + a custom date range
type TierLevel = 'free' | 'growth' | 'mid' | 'high';
type Preset = 'weekly' | 'monthly' | 'all-time';

const PRESET_LABELS: Record<Preset, string> = {
  weekly: 'This week',
  monthly: 'This month',
  'all-time': 'All time',
};

/** Map a UI preset to the backend `range` query param (note the `_` form). */
const PRESET_TO_RANGE: Record<Preset, AnalyticsRange> = {
  weekly: 'weekly',
  monthly: 'monthly',
  'all-time': 'all_time',
};

/** Presets each tier may request, in display order (mirrors the backend).
 * "Yesterday" was dropped from the UI: a one-day-ago window is rarely what
 * admins want, and the per-learner comparison block (lifetime metrics by
 * design) made it look like the filter was ignored. The backend still accepts
 * range=yesterday for API compatibility. */
const TIER_PRESETS: Record<TierLevel, Preset[]> = {
  free: ['weekly'],
  growth: ['weekly', 'monthly'],
  mid: ['weekly', 'monthly', 'all-time'],
  high: ['weekly', 'monthly', 'all-time'],
};

/**
 * Default preset for a tier: prefer "This month", then "This week" —
 * whichever the plan first allows. This keeps the default view on a *bounded*
 * range (which yields a trend + chart points) rather than the all-time
 * snapshot, which the API returns without trend/points by design.
 */
const DEFAULT_PRESET_PREFERENCE: Preset[] = ['monthly', 'weekly'];

/** Billing plan -> analytics tier level. Mirrors the backend `_PLAN_TIER`. */
export const PLAN_TIER_LEVEL: Record<TierId, TierLevel> = {
  free: 'free',
  growth: 'growth',
  pro: 'mid',
  enterprise: 'high',
};

/** The only tier level that may request an explicit start/end date range.
 *  Mirrors the backend `_TIER_ALLOWS_CUSTOM`. */
export const CUSTOM_RANGE_TIER: TierLevel = 'high';
const defaultPresetFor = (tier: TierLevel): Preset =>
  DEFAULT_PRESET_PREFERENCE.find((p) => TIER_PRESETS[tier].includes(p)) ??
  TIER_PRESETS[tier][0];

const fmtIso = (d: Date): string =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const getPresetDates = (preset: Preset): { start: string; end: string } => {
  const today = new Date();
  const end = fmtIso(today);
  switch (preset) {
    case 'weekly': {
      const d = new Date(today);
      d.setDate(d.getDate() - 7);
      return { start: fmtIso(d), end };
    }
    case 'monthly': {
      const d = new Date(today);
      d.setDate(d.getDate() - 30);
      return { start: fmtIso(d), end };
    }
    case 'all-time':
      return { start: '2025-01-12', end };
  }
};

const AnalyticsPage: React.FC = () => {
  const [selectedCourse, setSelectedCourse] = useState(ALL_COURSES);
  const [selectedProgram, setSelectedProgram] = useState('');
  const [selectedLearner, setSelectedLearner] = useState(ALL_LEARNERS);
  const [startDate, setStartDate] = useState(() => getPresetDates('monthly').start);
  const [endDate, setEndDate] = useState(() => getPresetDates('monthly').end);
  // The user's explicit preset choice (null → fall back to the tier default)
  // and whether they switched to a custom date range (Enterprise only).
  const [selectedPreset, setSelectedPreset] = useState<Preset | null>(null);
  const [isCustom, setIsCustom] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [csvError, setCsvError] = useState<string | null>(null);

  const { data: subscription } = useSubscription();
  const plan = subscription?.plan_id?.toLowerCase() ?? '';
  // Mirror the backend plan→tier policy: only Enterprise unlocks custom dates,
  // Program (cohort) analytics is Pro+ per the offer.
  const programAnalyticsAllowed = planHasFeature(FEATURE.programAnalytics, plan);

  // Pro gets all presets, Growth gets the 30-day dashboard, everyone else
  // (free/unknown) is weekly-only.
  const tierLevel: TierLevel = PLAN_TIER_LEVEL[normalizeTierId(plan)];

  // The preset actually in effect: a custom range overrides it; otherwise the
  // user's explicit choice, falling back to the tier default.
  const effectivePreset: Preset | null = isCustom
    ? null
    : selectedPreset ?? defaultPresetFor(tierLevel);

  // Resolve the range filter against the tenant's tier and current selection.
  // `range` (a preset) and a custom date pair are mutually exclusive on the API:
  //   free                  → always the weekly preset (the only one allowed)
  //   high + a custom range → explicit start/end dates
  //   otherwise             → the effective preset as a tier-gated `range`
  const rangeFilter: Pick<KpiFilters, 'range' | 'startDate' | 'endDate'> =
    tierLevel === 'free'
      ? { range: 'weekly' }
      : tierLevel === CUSTOM_RANGE_TIER && isCustom
        ? { startDate, endDate }
        : { range: PRESET_TO_RANGE[effectivePreset ?? defaultPresetFor(tierLevel)] };

  const selectPreset = (p: Preset) => {
    const { start, end } = getPresetDates(p);
    setStartDate(start);
    setEndDate(end);
    setSelectedPreset(p);
    setIsCustom(false);
  };
  const handleCustomStart = (v: string) => { setStartDate(v); setIsCustom(true); };
  const handleCustomEnd   = (v: string) => { setEndDate(v);   setIsCustom(true); };

  const { data: coursesData, isLoading: loadingCourses } = useCourses({ per_page: 100 });
  const courses = coursesData?.courses ?? [];
  const { data: programsData, isLoading: loadingPrograms } = usePrograms({ per_page: 100 });
  const programs = Array.isArray(programsData?.programs) ? programsData!.programs : [];
  const { data: programAnalytics, isLoading: loadingProgramAnalytics } =
    useProgramAnalytics(selectedProgram || undefined);
  // Includes the signed-in member as "(You)" — staff activity is filterable too.
  const { data: learners = [] } = useAssignableLearners();

  const courseId = selectedCourse === ALL_COURSES ? undefined : selectedCourse;
  const userId = selectedLearner === ALL_LEARNERS ? undefined : selectedLearner;

  const { data: kpis, isLoading } = useKpis({
    courseId,
    userId,
    ...rangeFilter,
  });

  const k = kpis?.kpis;

  // Sparkline points per KPI (null values → 0 so lines don't gap)
  const progressSpark = useMemo(
    () => (k?.avg_progress.points ?? []).map(p => ({ v: p.value ?? 0 })),
    [k]
  );
  const ahtSpark = useMemo(
    () => (k?.aht.points ?? []).map(p => ({ v: Math.round((p.value.active_seconds ?? 0) / 60) })),
    [k]
  );
  const feedbackSpark = useMemo(
    () => (k?.feedback.points ?? []).map(p => ({ v: (p.value ?? 0) * 2 })),
    [k]
  );
  const testSpark = useMemo(
    () => (k?.test_results.points ?? []).map(p => ({ v: p.value ?? 0 })),
    [k]
  );

  const learnerComparison: ResultsVsAverage | null = kpis?.learner
    ? {
        learner: { name: kpis.learner.name, found: kpis.learner.found },
        course: kpis.scope.course,
        metrics: kpis.learner.metrics,
      }
    : null;

  // "Excel exports" in the offer matrix — Pro+. The backend enforces the same
  // gate on /analytics/courses/<id>/learner-metrics; this only hides the button.
  const canExport = planHasFeature(FEATURE.dataExport, plan);

  const handleDownload = async () => {
    if ((!courseId && !selectedProgram) || !canExport) return;
    setDownloading(true);
    setCsvError(null);
    try {
      if (selectedProgram && programAnalytics) {
        await downloadProgramWorkbook(programAnalytics);
      } else if (courseId) {
        const data = await fetchCourseLearnerMetrics(courseId);
        await downloadWorkbook(data);
      }
    } catch {
      setCsvError('Could not generate the export. Please try again.');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <motion.div {...slideUp} className="space-y-6" data-testid="analytics-page">
      <PageHeader title="Analytics" />

      {/* Filters */}
      <div className="card p-4 flex flex-wrap items-end gap-3">
        <div className="flex flex-col gap-1.5 min-w-[180px]">
          <span className="text-xs font-medium text-text-secondary">Course</span>
          <EntityFilterSelect
            options={courses.map((c) => ({
              value: c.id,
              label: c.title,
              imageUrl: c.image_url,
            }))}
            value={selectedCourse === ALL_COURSES ? '' : selectedCourse}
            onChange={(v) => {
              setSelectedCourse(v || ALL_COURSES);
              if (v) setSelectedProgram('');
            }}
            allLabel="All Courses"
            loading={loadingCourses}
            data-testid="course-select-trigger"
            clearTestId="course-filter-clear"
          />
        </div>

        {/* Learning Programs — its own column; picking one swaps the page
            into program (cohort) analytics and clears the course pick.
            Program analytics is a Pro+ feature; below Pro the column shows
            a locked hint instead of a working selector. */}
        {!programAnalyticsAllowed ? (
          <div className="flex flex-col gap-1.5 min-w-[180px]">
            <span className="text-xs font-medium text-text-secondary">Learning Program</span>
            <div
              className="h-10 px-3 inline-flex items-center gap-2 rounded-lg border border-border-primary text-sm text-text-disabled cursor-not-allowed select-none"
              title="Program analytics are available from the Pro plan"
            >
              <LockClosedIcon className="w-4 h-4" aria-hidden="true" />
              Pro plan
            </div>
          </div>
        ) : (
        <div className="flex flex-col gap-1.5 min-w-[180px]">
          <span className="text-xs font-medium text-text-secondary">Learning Program</span>
          <EntityFilterSelect
            options={programs.map((p) => ({
              value: p.id,
              label: p.name,
              imageUrl: p.header_image,
            }))}
            value={selectedProgram}
            onChange={(v) => {
              setSelectedProgram(v || '');
              if (v) setSelectedCourse(ALL_COURSES);
            }}
            allLabel="All Programs"
            loading={loadingPrograms}
            data-testid="program-select-trigger"
            clearTestId="program-filter-clear"
          />
        </div>
        )}

        <div className="flex flex-col gap-1.5 min-w-[180px]">
          <span className="text-xs font-medium text-text-secondary">Learner</span>
          <EntityFilterSelect
            options={learners.map((l) => ({
              value: l.id,
              label: l.full_name || l.email,
              sublabel: l.full_name ? l.email : undefined,
              imageUrl: l.avatar_url,
            }))}
            value={selectedLearner === ALL_LEARNERS ? '' : selectedLearner}
            onChange={(v) => setSelectedLearner(v || ALL_LEARNERS)}
            allLabel="All Learners"
            data-testid="learner-select-trigger"
            clearTestId="learner-filter-clear"
          />
        </div>

        {/* Tier-gated period filter */}
        {tierLevel === 'free' ? (
          <div className="flex flex-col gap-1.5">
            <span className="text-xs font-medium text-text-secondary">Period</span>
            <div className="flex items-center gap-2 px-3 py-2.5 border border-[rgba(189,188,201,0.4)] rounded-lg bg-background-primary text-sm text-text-tertiary cursor-not-allowed select-none min-w-[120px]">
              <LockClosedIcon className="w-3.5 h-3.5 shrink-0" />
              <span>This week</span>
            </div>
          </div>
        ) : (
          <>
            <div className="flex flex-col gap-1.5">
              <span className="text-xs font-medium text-text-secondary">Period</span>
              <div className="flex items-center gap-1">
                {TIER_PRESETS[tierLevel].map((p) => (
                  <button
                    key={p}
                    type="button"
                    onClick={() => selectPreset(p)}
                    className={cn(
                      'px-3 py-2.5 rounded-full text-xs font-medium border transition-colors duration-[120ms] whitespace-nowrap',
                      effectivePreset === p
                        ? 'bg-primary-500 text-primary-ink border-primary-500'
                        : 'bg-background-primary text-text-secondary border-[rgba(189,188,201,0.4)] hover:border-border-primary/60'
                    )}
                  >
                    {PRESET_LABELS[p]}
                  </button>
                ))}
              </div>
            </div>

            {tierLevel === CUSTOM_RANGE_TIER && (
              <>
                <DatePicker
                  id="analytics-from"
                  label="From"
                  value={startDate}
                  onChange={handleCustomStart}
                  max={endDate || undefined}
                />
                <DatePicker
                  id="analytics-to"
                  label="To"
                  value={endDate}
                  onChange={handleCustomEnd}
                  min={startDate || undefined}
                />
              </>
            )}
          </>
        )}
      </div>

      {/* Program (cohort) analytics — takes over when a program is picked */}
      {selectedProgram ? (
        loadingProgramAnalytics || !programAnalytics ? (
          <div className="card p-12 text-center text-sm text-text-secondary" data-testid="program-analytics-loading">
            Loading program analytics…
          </div>
        ) : (
          <ProgramAnalyticsPanel
            data={programAnalytics}
            learnerFilter={selectedLearner === ALL_LEARNERS ? undefined : selectedLearner}
          />
        )
      ) : !courseId ? (
        <div className="card p-12 text-center" data-testid="analytics-empty-state">
          <svg
            className="w-10 h-10 mx-auto mb-3 text-text-tertiary"
            fill="none"
            stroke="currentColor"
            strokeWidth={1.5}
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z"
            />
          </svg>
          <p className="text-lg font-semibold text-text-primary">Select a Course or a Program</p>
          <p className="text-sm text-text-secondary mt-1">
            Pick a course or a learning program above — and optionally a
            learner — to see progress, handling time, feedback, and test
            results.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <KPICard
            title="Avg. Progress"
            value={formatPct(k?.avg_progress.current)}
            trend={formatTrend(k?.avg_progress.trend)}
            color="orange"
            loading={isLoading}
            sparkPoints={progressSpark}
          />
          <KPICard
            title="Average Handling Time"
            value={formatAht(k?.aht.current)}
            trend={formatTrend(k?.aht.trend)}
            color="purple"
            loading={isLoading}
            sparkPoints={ahtSpark}
          />
          <KPICard
            title="Learner Feedback"
            value={formatFeedback(k?.feedback.total_average)}
            trend={formatTrend(k?.feedback.trend)}
            color="gray"
            loading={isLoading}
            sparkPoints={feedbackSpark}
          />
          <KPICard
            title="Avg. Test Result"
            value={formatPct(k?.test_results.current)}
            trend={formatTrend(k?.test_results.trend)}
            color="green"
            loading={isLoading}
            sparkPoints={testSpark}
          />
        </div>
      )}

      {/* Learner-vs-cohort comparison (needs both a course and a learner) */}
      {courseId && userId && (
        <ResultsVsAverageChart data={learnerComparison ?? EMPTY_RESULTS} loading={isLoading} />
      )}

      {/* Detailed Excel export — per course (Pro/Enterprise) */}
      <div className="card p-6 text-center">
        {canExport ? (
          <>
            <button
              type="button"
              onClick={handleDownload}
              disabled={(!courseId && !selectedProgram) || downloading}
              className="btn-secondary px-8 py-4 inline-flex items-center gap-3 disabled:opacity-50"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              {downloading ? 'Preparing export…' : 'Download Detailed Statistics (Excel)'}
            </button>
            {!courseId && !selectedProgram && (
              <p className="body-small text-text-tertiary mt-3">
                Select a course or a learning program to download its
                per-learner metrics.
              </p>
            )}
            {csvError && <p className="body-small text-error-600 mt-3">{csvError}</p>}
          </>
        ) : (
          <div className="inline-flex items-center gap-2 text-text-tertiary">
            <LockClosedIcon className="w-4 h-4 shrink-0" />
            <span className="body-small">
              Detailed statistics export is available on Pro and Enterprise plans.
            </span>
          </div>
        )}
      </div>
    </motion.div>
  );
};

export default AnalyticsPage;
